import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
  type ParsedLocation,
} from '@tanstack/react-router';

import { useLinkToken } from '@/modules/auth/linkToken';
import { LoginPage } from '@/modules/auth/LoginPage';
import { redirectSearchSchema } from '@/modules/auth/redirectTarget';
import { auditLogSearchSchema } from '@/modules/audit/auditOptions';
import { locationsSearchSchema } from '@/modules/admin/locationsSearch';
import { contractsSearchSchema } from '@/modules/billing/contractsSearch';
import { invoicesSearchSchema } from '@/modules/billing/invoicesSearch';
import {
  exceptionHoursSearchSchema,
  exceptionLinesSearchSchema,
} from '@/modules/billing/exceptionHoursSearch';
import { entriesSearchSchema } from '@/modules/billing/entriesSearch';
import { periodsSearchSchema } from '@/modules/billing/periodsSearch';
import { notificationDeliveriesSearchSchema } from '@/modules/admin/notificationDeliveriesOptions';
import { organizationsSearchSchema } from '@/modules/admin/organizationsSearch';
import { helpPrintSearchSchema } from '@/modules/help/helpPrintSearch';
import { INDEX_PAGE } from '@/modules/help/markdown/paths';
import { knowledgeListSearchSchema } from '@/modules/knowledge/knowledgeListSearch';
import { knowledgeNewSearchSchema } from '@/modules/knowledge/knowledgeNewSearch';
import { dashboardSearchSchema } from '@/modules/dashboards/dashboardRanges';
import {
  reportDetailSearchSchema,
  reportDetailUrlSearch,
  reportsCatalogSearchSchema,
} from '@/modules/reports/reportSearch';
import { PortalLayout } from '@/modules/portal/PortalLayout';
import { approvalsSearchSchema } from '@/modules/approvals/approvalsSearch';
import { requestsSearchSchema } from '@/modules/requests/requestsSearch';
import { ticketNewSearchSchema, ticketsSearchSchema } from '@/modules/tickets/ticketOptions';
import { searchResultsSearchSchema } from '@/modules/search/searchResultsSearch';
import { NotFoundPage } from '@/modules/system/NotFoundPage';
import { RouteErrorPage } from '@/shared/components/RouteErrorPage';
import { lazyPage } from '@/app/lazyPage';
import { AppShellLayout } from '@/shared/components/AppShellLayout';
import { portalTarget, shouldUsePortal } from '@/shared/navigation/manifest';
import { isPlatformAdminSession, loadSession } from '@/shared/session/session';
import { PlatformLayout } from '@/modules/platform/PlatformLayout';
import { platformSupportAccessSearchSchema } from '@/modules/platform/platformSupportAccessSearch';
import { platformAuditSearchSchema } from '@/modules/platform/platformAuditSearch';
import { myTimeSearchSchema } from '@/modules/time/myTimeSearch';
import { timeApprovalsSearchSchema } from '@/modules/time/timeApprovalsSearch';
import { clientSearchSchema } from '@/modules/clients/clientSearch';
import { ticketSearchSchema } from '@/modules/tickets/ticketSearch';

// `AM-197`: every page but the login and the error pages is its own chunk.
const AcceptInvitationPage = lazyPage(
  () => import('@/modules/auth/AcceptInvitationPage'),
  'AcceptInvitationPage',
);
const ForgotPasswordPage = lazyPage(
  () => import('@/modules/auth/ForgotPasswordPage'),
  'ForgotPasswordPage',
);
const ResetPasswordPage = lazyPage(
  () => import('@/modules/auth/ResetPasswordPage'),
  'ResetPasswordPage',
);
const TenantSelectPage = lazyPage(
  () => import('@/modules/auth/TenantSelectPage'),
  'TenantSelectPage',
);
const AdminIndexPage = lazyPage(() => import('@/modules/admin/AdminIndexPage'), 'AdminIndexPage');
const AgentProfilesPage = lazyPage(
  () => import('@/modules/admin/AgentProfilesPage'),
  'AgentProfilesPage',
);
const ApprovalRulesPage = lazyPage(
  () => import('@/modules/admin/ApprovalRulesPage'),
  'ApprovalRulesPage',
);
const AssetTypesPage = lazyPage(() => import('@/modules/admin/AssetTypesPage'), 'AssetTypesPage');
const AutomationPage = lazyPage(() => import('@/modules/admin/AutomationPage'), 'AutomationPage');
const SlaPoliciesPage = lazyPage(
  () => import('@/modules/admin/SlaPoliciesPage'),
  'SlaPoliciesPage',
);
const SupportAccessAdminPage = lazyPage(
  () => import('@/modules/admin/SupportAccessAdminPage'),
  'SupportAccessAdminPage',
);
const DataExportPage = lazyPage(() => import('@/modules/admin/DataExportPage'), 'DataExportPage');
const ApprovalsPage = lazyPage(() => import('@/modules/approvals/ApprovalsPage'), 'ApprovalsPage');
const AuditLogPage = lazyPage(() => import('@/modules/audit/AuditLogPage'), 'AuditLogPage');
const BusinessHoursPage = lazyPage(
  () => import('@/modules/admin/BusinessHoursPage'),
  'BusinessHoursPage',
);
const CategoriesPage = lazyPage(() => import('@/modules/admin/CategoriesPage'), 'CategoriesPage');
const CiTypesPage = lazyPage(() => import('@/modules/admin/CiTypesPage'), 'CiTypesPage');
const DepartmentsPage = lazyPage(
  () => import('@/modules/admin/DepartmentsPage'),
  'DepartmentsPage',
);
const HolidaysPage = lazyPage(() => import('@/modules/admin/HolidaysPage'), 'HolidaysPage');
const LocationsPage = lazyPage(() => import('@/modules/admin/LocationsPage'), 'LocationsPage');
const NotificationDeliveriesPage = lazyPage(
  () => import('@/modules/admin/NotificationDeliveriesPage'),
  'NotificationDeliveriesPage',
);
const NotificationRulesPage = lazyPage(
  () => import('@/modules/admin/NotificationRulesPage'),
  'NotificationRulesPage',
);
const NotificationTemplatesPage = lazyPage(
  () => import('@/modules/admin/NotificationTemplatesPage'),
  'NotificationTemplatesPage',
);
const OrganizationsPage = lazyPage(
  () => import('@/modules/admin/OrganizationsPage'),
  'OrganizationsPage',
);
const PersonsPage = lazyPage(() => import('@/modules/admin/PersonsPage'), 'PersonsPage');
const TechnicianRolesPage = lazyPage(
  () => import('@/modules/admin/TechnicianRolesPage'),
  'TechnicianRolesPage',
);
const PrioritiesPage = lazyPage(() => import('@/modules/admin/PrioritiesPage'), 'PrioritiesPage');
const ServiceAreasPage = lazyPage(
  () => import('@/modules/admin/ServiceAreasPage'),
  'ServiceAreasPage',
);
const MaintenancePlansPage = lazyPage(
  () => import('@/modules/admin/MaintenancePlansPage'),
  'MaintenancePlansPage',
);
const ServiceOfferingsPage = lazyPage(
  () => import('@/modules/admin/ServiceOfferingsPage'),
  'ServiceOfferingsPage',
);
const ServicesPage = lazyPage(() => import('@/modules/admin/ServicesPage'), 'ServicesPage');
const StatusesPage = lazyPage(() => import('@/modules/admin/StatusesPage'), 'StatusesPage');
const TaskTypesPage = lazyPage(() => import('@/modules/admin/TaskTypesPage'), 'TaskTypesPage');
const BillingRulesPage = lazyPage(
  () => import('@/modules/admin/BillingRulesPage'),
  'BillingRulesPage',
);
const ContractDetailPage = lazyPage(
  () => import('@/modules/billing/ContractDetailPage'),
  'ContractDetailPage',
);
const ContractsListPage = lazyPage(
  () => import('@/modules/billing/ContractsListPage'),
  'ContractsListPage',
);
const BillableEntriesPage = lazyPage(
  () => import('@/modules/billing/BillableEntriesPage'),
  'BillableEntriesPage',
);
const PeriodExceptionHoursPage = lazyPage(
  () => import('@/modules/billing/PeriodExceptionHoursPage'),
  'PeriodExceptionHoursPage',
);
const PeriodExceptionLinesPage = lazyPage(
  () => import('@/modules/billing/PeriodExceptionLinesPage'),
  'PeriodExceptionLinesPage',
);
const BillingPeriodDetailPage = lazyPage(
  () => import('@/modules/billing/BillingPeriodDetailPage'),
  'BillingPeriodDetailPage',
);
const BillingPeriodsPage = lazyPage(
  () => import('@/modules/billing/BillingPeriodsPage'),
  'BillingPeriodsPage',
);
const InvoiceDetailPage = lazyPage(
  () => import('@/modules/billing/InvoiceDetailPage'),
  'InvoiceDetailPage',
);
const InvoicesListPage = lazyPage(
  () => import('@/modules/billing/InvoicesListPage'),
  'InvoicesListPage',
);
const PendingToInvoicePage = lazyPage(
  () => import('@/modules/billing/PendingToInvoicePage'),
  'PendingToInvoicePage',
);
const RateCardsPage = lazyPage(() => import('@/modules/billing/RateCardsPage'), 'RateCardsPage');
const TaxRatesPage = lazyPage(() => import('@/modules/admin/TaxRatesPage'), 'TaxRatesPage');
const BrandingPage = lazyPage(() => import('@/modules/admin/BrandingPage'), 'BrandingPage');
const FiscalIssuerPage = lazyPage(
  () => import('@/modules/admin/FiscalIssuerPage'),
  'FiscalIssuerPage',
);
const SecurityPolicyPage = lazyPage(
  () => import('@/modules/admin/SecurityPolicyPage'),
  'SecurityPolicyPage',
);
const CapabilitiesPage = lazyPage(
  () => import('@/modules/admin/CapabilitiesPage'),
  'CapabilitiesPage',
);
const AccountSecurityPage = lazyPage(
  () => import('@/modules/account/AccountSecurityPage'),
  'AccountSecurityPage',
);
const SequencesPage = lazyPage(() => import('@/modules/admin/SequencesPage'), 'SequencesPage');
const InvoiceNumberingPage = lazyPage(
  () => import('@/modules/admin/SequencesPage'),
  'InvoiceNumberingPage',
);
const TeamsPage = lazyPage(() => import('@/modules/admin/TeamsPage'), 'TeamsPage');
const TransitionsPage = lazyPage(
  () => import('@/modules/admin/TransitionsPage'),
  'TransitionsPage',
);
const RolesPage = lazyPage(() => import('@/modules/admin/RolesPage'), 'RolesPage');
const UsersPage = lazyPage(() => import('@/modules/admin/UsersPage'), 'UsersPage');
const VendorsPage = lazyPage(() => import('@/modules/admin/VendorsPage'), 'VendorsPage');
const WorkTypesPage = lazyPage(() => import('@/modules/admin/WorkTypesPage'), 'WorkTypesPage');
const AssetDetailPage = lazyPage(
  () => import('@/modules/assets/AssetDetailPage'),
  'AssetDetailPage',
);
const AssetsListPage = lazyPage(() => import('@/modules/assets/AssetsListPage'), 'AssetsListPage');
const DashboardPage = lazyPage(() => import('@/modules/dashboards/DashboardPage'), 'DashboardPage');
const KnowledgeDetailPage = lazyPage(
  () => import('@/modules/knowledge/KnowledgeDetailPage'),
  'KnowledgeDetailPage',
);
const KnowledgeEditPage = lazyPage(
  () => import('@/modules/knowledge/KnowledgeFormPage'),
  'KnowledgeEditPage',
);
const KnowledgeNewPage = lazyPage(
  () => import('@/modules/knowledge/KnowledgeFormPage'),
  'KnowledgeNewPage',
);
const KnowledgeListPage = lazyPage(
  () => import('@/modules/knowledge/KnowledgeListPage'),
  'KnowledgeListPage',
);
const NotificationsPage = lazyPage(
  () => import('@/modules/notifications/NotificationsPage'),
  'NotificationsPage',
);
const ReportDetailPage = lazyPage(
  () => import('@/modules/reports/ReportDetailPage'),
  'ReportDetailPage',
);
const ReportsCatalogPage = lazyPage(
  () => import('@/modules/reports/ReportsCatalogPage'),
  'ReportsCatalogPage',
);
const MyRequestsPage = lazyPage(() => import('@/modules/portal/MyRequestsPage'), 'MyRequestsPage');
const NewRequestPage = lazyPage(() => import('@/modules/portal/NewRequestPage'), 'NewRequestPage');
const PortalKnowledgeArticlePage = lazyPage(
  () => import('@/modules/portal/PortalKnowledgeArticlePage'),
  'PortalKnowledgeArticlePage',
);
const PortalKnowledgeListPage = lazyPage(
  () => import('@/modules/portal/PortalKnowledgeListPage'),
  'PortalKnowledgeListPage',
);
const RequestDetailPage = lazyPage(
  () => import('@/modules/portal/RequestDetailPage'),
  'RequestDetailPage',
);
const MyTicketsPage = lazyPage(() => import('@/modules/portal/MyTicketsPage'), 'MyTicketsPage');
const PortalTicketDetailPage = lazyPage(
  () => import('@/modules/portal/PortalTicketDetailPage'),
  'PortalTicketDetailPage',
);
const PortalApprovalsPage = lazyPage(
  () => import('@/modules/portal/PortalApprovalsPage'),
  'PortalApprovalsPage',
);
const PortalApprovalDetailPage = lazyPage(
  () => import('@/modules/portal/PortalApprovalDetailPage'),
  'PortalApprovalDetailPage',
);
const TriageQueuePage = lazyPage(
  () => import('@/modules/requests/TriageQueuePage'),
  'TriageQueuePage',
);
const SearchResultsPage = lazyPage(
  () => import('@/modules/search/SearchResultsPage'),
  'SearchResultsPage',
);
const MyWorkPage = lazyPage(() => import('@/modules/my-work/MyWorkPage'), 'MyWorkPage');
const TasksListPage = lazyPage(() => import('@/modules/tickets/TasksListPage'), 'TasksListPage');
const ClientsListPage = lazyPage(
  () => import('@/modules/clients/ClientsListPage'),
  'ClientsListPage',
);
const ClientDetailPage = lazyPage(
  () => import('@/modules/clients/ClientDetailPage'),
  'ClientDetailPage',
);
const MyTimePage = lazyPage(() => import('@/modules/time/MyTimePage'), 'MyTimePage');
const TimeApprovalsPage = lazyPage(
  () => import('@/modules/time/TimeApprovalsPage'),
  'TimeApprovalsPage',
);
const TicketNewPage = lazyPage(() => import('@/modules/tickets/TicketNewPage'), 'TicketNewPage');
const TicketsListPage = lazyPage(
  () => import('@/modules/tickets/TicketsListPage'),
  'TicketsListPage',
);
const TicketWorkspacePage = lazyPage(
  () => import('@/modules/tickets/TicketWorkspacePage'),
  'TicketWorkspacePage',
);
const SystemStatusPage = lazyPage(
  () => import('@/modules/system/SystemStatusPage'),
  'SystemStatusPage',
);
const PlatformIndexPage = lazyPage(
  () => import('@/modules/platform/PlatformIndexPage'),
  'PlatformIndexPage',
);
const PlatformTenantsPage = lazyPage(
  () => import('@/modules/platform/PlatformTenantsPage'),
  'PlatformTenantsPage',
);
const PlatformTenantDetailPage = lazyPage(
  () => import('@/modules/platform/PlatformTenantDetailPage'),
  'PlatformTenantDetailPage',
);
const PlatformSupportAccessPage = lazyPage(
  () => import('@/modules/platform/PlatformSupportAccessPage'),
  'PlatformSupportAccessPage',
);
const PlatformAuditPage = lazyPage(
  () => import('@/modules/platform/PlatformAuditPage'),
  'PlatformAuditPage',
);
const PlatformFeatureFlagsPage = lazyPage(
  () => import('@/modules/platform/PlatformFeatureFlagsPage'),
  'PlatformFeatureFlagsPage',
);
// The manual (`docs/manual`) travels with the console but weighs nothing until somebody opens it:
// these chunks hold its pages and screenshots.
const HelpPage = lazyPage(() => import('@/modules/help/HelpPage'), 'HelpPage');
const HelpPrintPage = lazyPage(() => import('@/modules/help/HelpPrintPage'), 'HelpPrintPage');
const PortalHelpPage = lazyPage(() => import('@/modules/help/PortalHelpPage'), 'PortalHelpPage');
const PlatformHelpPage = lazyPage(
  () => import('@/modules/help/PlatformHelpPage'),
  'PlatformHelpPage',
);

/**
 * D13: TanStack Router with code-based routes. The anonymous surface (`/login/*`) and the
 * console live side by side under the root; the console's layout route is where the session
 * guard runs (13 §2.5), so every page inside it can assume an active tenant.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
  // A route whose data failed to load shows the error page instead of blanking the console.
  errorComponent: RouteErrorPage,
});

/**
 * The guard of everything the console serves (13 §2.2): a session with an active tenant, and a
 * requester whose permissions open nothing in the console goes to the portal, not to a 403 they
 * cannot act on; the link of a ticket or an approval, to the portal's page of it. The printable
 * manual sits outside the console's shell and needs the same guard.
 */
async function requireConsoleSession({
  context,
  location,
}: {
  context: RouterContext;
  location: ParsedLocation;
}) {
  const session = await loadSession(context.queryClient);
  if (session === null) {
    throw redirect({ to: '/login', search: { redirect: location.href } });
  }
  if (session.activeTenant === null) {
    throw redirect({ to: '/login/tenant', search: { redirect: location.href } });
  }
  const permissions = new Set(session.permissions);
  if (shouldUsePortal(permissions)) {
    throw redirect(
      portalTarget(location.pathname, permissions, location.search as Record<string, unknown>),
    );
  }
  return { session };
}

const consoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'console',
  component: AppShellLayout,
  beforeLoad: requireConsoleSession,
});

const indexRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/',
  validateSearch: dashboardSearchSchema,
  component: DashboardPage,
});

const systemRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/system',
  component: SystemStatusPage,
});

/** The notification centre of step 17f (13 §6.2, §5.4): the recipient's own inbox and mute switches. */
const notificationsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/notifications',
  component: NotificationsPage,
});

/** The technician's phone space (13 §5.2, `§39`): three tabs over the same "mine" pages below. */
const myWorkRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/my-work',
  component: MyWorkPage,
});

/** The work of step 8 (20 §7.2): the queue of tickets and the technician's workspace (13 §4.3). */
const ticketsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/tickets',
  validateSearch: ticketsSearchSchema,
  component: TicketsListPage,
});

const tasksRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/tasks',
  component: TasksListPage,
});

/** The customers with their open tickets and hours, and the file of each one. */
const clientsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/clients',
  component: ClientsListPage,
});

const clientDetailRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/clients/$organizationId',
  validateSearch: clientSearchSchema,
  component: ClientDetailPage,
});

const timeEntriesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/time-entries',
  validateSearch: myTimeSearchSchema,
  component: MyTimePage,
});

const timeApprovalsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/time-entries/approvals',
  validateSearch: timeApprovalsSearchSchema,
  component: TimeApprovalsPage,
});

/**
 * The generic bandeja of the approvals engine (`13 §4.7`, `AM-133`), separate from
 * `/time-entries/approvals` (`I-25`). The notice that asks an agent to decide opens it with that
 * approval's drawer open (`approvalRequestId`).
 */
const approvalsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/approvals',
  validateSearch: approvalsSearchSchema,
  component: ApprovalsPage,
});

/**
 * The alta of a ticket by an agent (13 route table line 226): a route of its own, before the detail.
 * `?replaces=<id>` opens it from another ticket, which the new one replaces (`AM-302`).
 */
const ticketNewRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/tickets/new',
  validateSearch: ticketNewSearchSchema,
  component: TicketNewPage,
});

const ticketRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/tickets/$ticketId',
  validateSearch: ticketSearchSchema,
  component: function TicketRoute() {
    const { ticketId } = ticketRoute.useParams();
    const { tab } = ticketRoute.useSearch();
    return <TicketWorkspacePage ticketId={ticketId} initialTab={tab} />;
  },
});

/** The asset inventory of step 9 (13 §4.9): the grid and the detail behind it. */
const assetsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/assets',
  component: AssetsListPage,
});

const assetDetailRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/assets/$assetId',
  component: AssetDetailPage,
});

/** The triage bench of step 7 (20 §7.2); the portal's counterpart inside the console. */
const requestsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/requests',
  validateSearch: requestsSearchSchema,
  component: TriageQueuePage,
});

/** The knowledge base of `AM-132 point 11–12`: search/list, alta, detail and its editor. */
const knowledgeRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/knowledge',
  validateSearch: knowledgeListSearchSchema,
  component: KnowledgeListPage,
});

const knowledgeNewRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/knowledge/new',
  validateSearch: knowledgeNewSearchSchema,
  component: KnowledgeNewPage,
});

const knowledgeArticleRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/knowledge/$articleId',
  component: KnowledgeDetailPage,
});

const knowledgeEditRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/knowledge/$articleId/edit',
  component: KnowledgeEditPage,
});

/** Global search (`AM-132 point 10–11`): where the palette's "Ver todos los resultados" leads. */
const searchRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/search',
  validateSearch: searchResultsSearchSchema,
  component: SearchResultsPage,
});

const adminRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin',
  component: AdminIndexPage,
});
const adminUsersRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/users',
  component: UsersPage,
});
const adminRolesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/roles',
  component: RolesPage,
});
const adminOrganizationsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/organizations',
  validateSearch: organizationsSearchSchema,
  component: OrganizationsPage,
});
const adminTeamsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/teams',
  component: TeamsPage,
});
const adminDepartmentsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/departments',
  component: DepartmentsPage,
});
const adminLocationsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/locations',
  validateSearch: locationsSearchSchema,
  component: LocationsPage,
});
const adminPersonsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/persons',
  component: PersonsPage,
});
const adminTechnicianRolesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/technician-roles',
  component: TechnicianRolesPage,
});
const adminAgentProfilesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/agent-profiles',
  component: AgentProfilesPage,
});
const adminVendorsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/vendors',
  component: VendorsPage,
});

/** The thirteen catalogs of step 6 (05b §5 to §9 and §11), one route each. */
const adminServicesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/services',
  component: ServicesPage,
});
const adminServiceAreasRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/service-areas',
  component: ServiceAreasPage,
});
const adminMaintenancePlansRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/maintenance-plans',
  component: MaintenancePlansPage,
});
const adminServiceOfferingsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/service-offerings',
  component: ServiceOfferingsPage,
});
const adminBusinessHoursRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/business-hours',
  component: BusinessHoursPage,
});
const adminHolidaysRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/holidays',
  component: HolidaysPage,
});
const adminTransitionsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/transitions',
  component: TransitionsPage,
});
const adminPrioritiesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/priorities',
  component: PrioritiesPage,
});
const adminStatusesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/statuses',
  component: StatusesPage,
});
const adminCategoriesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/categories',
  component: CategoriesPage,
});
const adminWorkTypesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/work-types',
  component: WorkTypesPage,
});
const adminTaskTypesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/task-types',
  component: TaskTypesPage,
});
const adminAssetTypesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/asset-types',
  component: AssetTypesPage,
});
const adminCiTypesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/ci-types',
  component: CiTypesPage,
});
/** The "Comunicación" group of `/admin` (13 §4.12, AM-129): templates and rules, one route each. */
const adminNotificationTemplatesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/notifications',
  component: NotificationTemplatesPage,
});
const adminNotificationRulesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/notification-rules',
  component: NotificationRulesPage,
});
/** What became of each notice, and why the ones that did not go out did not (`GET /notification-deliveries`). */
const adminNotificationDeliveriesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/notification-deliveries',
  validateSearch: notificationDeliveriesSearchSchema,
  component: NotificationDeliveriesPage,
});
/** The "Flujo de trabajo" group of `/admin` (`13 §4.12`, `AM-133`): condition → action rules, one route each. */
const adminApprovalRulesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/approval-rules',
  component: ApprovalRulesPage,
});
const adminAutomationRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/automation',
  component: AutomationPage,
});
/** `AM-350`: the filters of the list live in the URL, so the file of a customer opens it narrowed to it. */
const billingContractsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/billing/contracts',
  validateSearch: contractsSearchSchema,
  component: ContractsListPage,
});
const billingContractDetailRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/billing/contracts/$contractId',
  component: ContractDetailPage,
});
const billingRateCardsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/billing/rate-cards',
  component: RateCardsPage,
});
/** `AM-368`: the filters of the list live in the URL, so "Ver todo" of "Períodos abiertos" opens it narrowed. */
const billingPeriodsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/billing/periods',
  validateSearch: periodsSearchSchema,
  component: BillingPeriodsPage,
});
const billingPeriodDetailRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/billing/periods/$periodId',
  component: BillingPeriodDetailPage,
});
const billingEntriesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/billing/billable-entries',
  validateSearch: entriesSearchSchema,
  component: BillableEntriesPage,
});
/** `AM-552`: the hours one row of the exceptions counts, of a period or of every period not archived. */
const billingExceptionHoursRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/billing/exception-hours',
  validateSearch: exceptionHoursSearchSchema,
  component: PeriodExceptionHoursPage,
});
/** `AM-723`: the items or the parts and trips one row of the exceptions counts, of a period or of every one not archived. */
const billingExceptionLinesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/billing/exception-lines',
  validateSearch: exceptionLinesSearchSchema,
  component: PeriodExceptionLinesPage,
});
/**
 * `AM-350`: the filters of the list live in the URL, so "Ver todas" of "Sin cobrar" opens the documents
 * owed and the file of a customer its own.
 */
const billingInvoicesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/billing/invoices',
  validateSearch: invoicesSearchSchema,
  component: InvoicesListPage,
});
const billingInvoiceDetailRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/billing/invoices/$invoiceId',
  component: InvoiceDetailPage,
});
/**
 * `AM-350`: every ticket of "Pendiente de facturar", which the box of Inicio cuts at twenty. Its "Ver
 * todos" is the way in; the menu has no item for it.
 */
const billingPendingToInvoiceRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/billing/pending-to-invoice',
  component: PendingToInvoicePage,
});
const adminBillingRulesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/billing-rules',
  component: BillingRulesPage,
});
const adminTaxRatesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/tax-rates',
  component: TaxRatesPage,
});
/** `AM-171`: the ten numbering counters of the tenant (07 §4.5, 05a §6.4). */
const adminSequencesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/sequences',
  component: SequencesPage,
});
/** `AM-440`: "Numeración de comprobantes", the three counters of the comprobantes, under `admin.billing.configure`. */
const adminInvoiceNumberingRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/invoice-numbering',
  component: InvoiceNumberingPage,
});
/** `AM-511`: "Datos fiscales del emisor", the company's identity as issuer, apart from the brand. */
const adminFiscalIssuerRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/fiscal-issuer',
  component: FiscalIssuerPage,
});
/** `AM-173`: the tenant's email sender, support contact and appearance (07 §4.5, 13 §6.5). */
const adminBrandingRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/branding',
  component: BrandingPage,
});
/** `AM-198`: whether every user of the tenant needs a second factor (08 §2.10). */
const adminSecurityRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/security',
  component: SecurityPolicyPage,
});
/** `AM-202`: whether the tenant bills commercially and whether an hour needs approval. */
const adminCapabilitiesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/capabilities',
  component: CapabilitiesPage,
});
/** `AM-198`: the signed-in identity's own second factor, inside each of the three shells. */
const accountSecurityRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/account/security',
  component: AccountSecurityPage,
});
/** The "Sistema" group of `/admin` (`13 §4.12`): the tenant's own trail, `audit.view`. */
const adminAuditRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/audit',
  validateSearch: auditLogSearchSchema,
  component: AuditLogPage,
});

/** `AM-226`: the export of every datum of the tenant, `admin.tenant.export` (14 §13). */
const adminDataExportRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/data-export',
  component: DataExportPage,
});

/** Tenant side of the boundary (`AM-134` point 6): approve, reject or revoke a support access grant. */
const adminSupportAccessRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/support-access',
  component: SupportAccessAdminPage,
});

/** R-10: the SLA policies of `07 §4.12`, `admin.sla.manage`. */
const adminSlaPoliciesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/admin/sla-policies',
  component: SlaPoliciesPage,
});

/** The catalogue of step 18e (13 §4.11): the ten reports, filed by family. */
const reportsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/reports',
  validateSearch: reportsCatalogSearchSchema,
  component: ReportsCatalogPage,
});

const reportDetailRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/reports/$reportKey',
  validateSearch: reportDetailSearchSchema,
  search: { middlewares: [reportDetailUrlSearch] },
  component: ReportDetailPage,
});

/**
 * `AM-490`: the four entries of the menu Reportes, one per family (`AM-470`). A plain path, not a
 * query string, is what `NavItem.to` can carry, so each redirects to the one the catalogue's filter
 * actually reads (13 §4.11). They are static, so they win over `/reports/$reportKey`, and no report
 * key takes one of these names.
 */
const reportsOperationalRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/reports/operational',
  beforeLoad: () => {
    throw redirect({ to: '/reports', search: { family: 'operational' } });
  },
});

const reportsHoursRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/reports/hours',
  beforeLoad: () => {
    throw redirect({ to: '/reports', search: { family: 'hours' } });
  },
});

const reportsCostsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/reports/costs',
  beforeLoad: () => {
    throw redirect({ to: '/reports', search: { family: 'costs' } });
  },
});

const reportsBillingRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/reports/billing',
  beforeLoad: () => {
    throw redirect({ to: '/reports', search: { family: 'billing' } });
  },
});

/**
 * The manual inside the console: `/ayuda` is its index and `/ayuda/<page>` a page of it
 * (`/ayuda/referencia/tickets-y-solicitudes`), the address of a page being its file without the
 * extension. The splat takes the folder of the reference along with the name.
 *
 * The index is `/ayuda/` (a route of type "index"), not `/ayuda`: `/ayuda/$` also matches an empty
 * splat and, ranked by length, beat the plain `/ayuda`, which then never matched anything and made
 * the router warn on every link to the manual (ronda 4 del manual, punto 8).
 */
const helpIndexRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/ayuda/',
  component: function HelpIndexRoute() {
    return <HelpPage page={INDEX_PAGE} />;
  },
});

const helpPageRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/ayuda/$',
  component: function HelpPageRoute() {
    const { _splat } = helpPageRoute.useParams();
    return <HelpPage page={_splat === undefined || _splat === '' ? INDEX_PAGE : _splat} />;
  },
});

/** Catch-all: any unknown path renders inside the shell instead of blanking it. */
const catchAllRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '$',
  component: NotFoundPage,
});

/**
 * The whole manual on paper (`/ayuda/imprimir`, or one page with `?pagina=`): outside the console's
 * shell, so it has no menu or header to hide, behind the console's own guard.
 */
const helpPrintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ayuda/imprimir',
  validateSearch: helpPrintSearchSchema,
  beforeLoad: requireConsoleSession,
  component: function HelpPrintRoute() {
    const search = helpPrintRoute.useSearch();
    return <HelpPrintPage search={search} />;
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: redirectSearchSchema,
  beforeLoad: async ({ context }) => {
    const session = await loadSession(context.queryClient);
    if (session !== null && session.activeTenant !== null) {
      throw redirect({ to: '/' });
    }
  },
  component: function LoginRoute() {
    const { redirect: redirectTo } = loginRoute.useSearch();
    return <LoginPage redirectTo={redirectTo} />;
  },
});

const tenantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/tenant',
  validateSearch: redirectSearchSchema,
  beforeLoad: async ({ context, search }) => {
    const session = await loadSession(context.queryClient);
    if (session === null) {
      // The page the person was headed to survives a session that expired on the selector too.
      throw redirect({ to: '/login', search });
    }
  },
  component: function TenantRoute() {
    const { redirect: redirectTo } = tenantRoute.useSearch();
    return <TenantSelectPage redirectTo={redirectTo} />;
  },
});

const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

/** The email links carry the signed token in the fragment (08 §2.2.2, §2.6, `AM-190`): see `useLinkToken`. */
const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: function ResetRoute() {
    return <ResetPasswordPage token={useLinkToken()} />;
  },
});

const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invitation',
  component: function InvitationRoute() {
    return <AcceptInvitationPage token={useLinkToken()} />;
  },
});

/**
 * The requester's portal (13 §3.2): its own shell - no side menu, content centred, no tenant
 * selector - behind the same session guard as the console, because a requester is a user of the
 * tenant like any other.
 */
const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'portal',
  component: PortalLayout,
  beforeLoad: async ({ context, location }) => {
    const session = await loadSession(context.queryClient);
    if (session === null) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    if (session.activeTenant === null) {
      throw redirect({ to: '/login/tenant', search: { redirect: location.href } });
    }
    return { session };
  },
});

const portalIndexRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/portal',
  component: MyRequestsPage,
});

const portalNewRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/portal/new',
  component: NewRequestPage,
});

const portalRequestRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/portal/requests/$requestId',
  component: function PortalRequestRoute() {
    const { requestId } = portalRequestRoute.useParams();
    return <RequestDetailPage requestId={requestId} />;
  },
});

/** The customer's own tickets and their conversation, where a converted request leads. */
const portalTicketsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/portal/tickets',
  component: MyTicketsPage,
});

const portalTicketRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/portal/tickets/$ticketId',
  component: function PortalTicketRoute() {
    const { ticketId } = portalTicketRoute.useParams();
    return <PortalTicketDetailPage ticketId={ticketId} />;
  },
});

/** "Mis aprobaciones" (`AM-238`): the approver of the customer's side decides here, and the notice leads here. */
const portalApprovalsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/portal/approvals',
  component: PortalApprovalsPage,
});

const portalApprovalRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/portal/approvals/$approvalId',
  component: function PortalApprovalRoute() {
    const { approvalId } = portalApprovalRoute.useParams();
    return <PortalApprovalDetailPage approvalId={approvalId} />;
  },
});

/** The public knowledge base of `13 §4.2.2`: published articles the portal's own visibility allows. */
const portalKnowledgeRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/portal/knowledge',
  validateSearch: knowledgeListSearchSchema,
  component: function PortalKnowledgeRoute() {
    const search = portalKnowledgeRoute.useSearch();
    return <PortalKnowledgeListPage search={search} />;
  },
});

const portalKnowledgeArticleRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/portal/knowledge/$articleId',
  component: function PortalKnowledgeArticleRoute() {
    const { articleId } = portalKnowledgeArticleRoute.useParams();
    return <PortalKnowledgeArticlePage articleId={articleId} />;
  },
});

/**
 * The platform area (13 §3.4, `AM-134` point 11): its own root, apart from the console and the
 * portal, because a Platform Administrator with no tenant membership at all - `GET /me` cannot
 * even be called for one, since it needs a tenant context (`RequireTenant`) - still has to reach
 * it. The guard reads `session.platform.isPlatformAdmin` from `GET /auth/session` (ISO-24,
 * `AM-134` point 11), which asks for nothing beyond an authenticated session (`AM-134` point 7).
 */
const platformRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'platform',
  component: PlatformLayout,
  beforeLoad: async ({ context, location }) => {
    const session = await loadSession(context.queryClient);
    if (session === null) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    if (!isPlatformAdminSession(session)) {
      throw redirect({ to: '/' });
    }
    return { session };
  },
});

const platformIndexRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform',
  component: PlatformIndexPage,
});

const platformTenantsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/tenants',
  component: PlatformTenantsPage,
});

const platformTenantDetailRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/tenants/$tenantId',
  component: function PlatformTenantDetailRoute() {
    const { tenantId } = platformTenantDetailRoute.useParams();
    return <PlatformTenantDetailPage tenantId={tenantId} />;
  },
});

const platformSupportAccessRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/support-access',
  validateSearch: platformSupportAccessSearchSchema,
  component: function PlatformSupportAccessRoute() {
    const search = platformSupportAccessRoute.useSearch();
    return <PlatformSupportAccessPage search={search} />;
  },
});

const platformAuditRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/audit',
  validateSearch: platformAuditSearchSchema,
  component: function PlatformAuditRoute() {
    const navigate = useNavigate();
    const search = platformAuditRoute.useSearch();
    return (
      <PlatformAuditPage
        search={search}
        onSearchChange={(patch) =>
          void navigate({
            to: '/platform/audit',
            search: (previous) => ({ ...previous, ...patch }),
            replace: true,
          })
        }
      />
    );
  },
});

const platformFeatureFlagsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/feature-flags',
  component: PlatformFeatureFlagsPage,
});

const platformAccountSecurityRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/account/security',
  component: AccountSecurityPage,
});

const portalAccountSecurityRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/portal/account/security',
  component: AccountSecurityPage,
});

/** The portal's chapter of the manual: the only one written for a customer. */
const portalHelpRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/portal/ayuda',
  component: PortalHelpPage,
});

/** The platform's chapter of the manual. */
const platformHelpRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/ayuda',
  component: PlatformHelpPage,
});

const routeTree = rootRoute.addChildren([
  consoleRoute.addChildren([
    indexRoute,
    systemRoute,
    notificationsRoute,
    requestsRoute,
    knowledgeRoute,
    knowledgeNewRoute,
    knowledgeArticleRoute,
    knowledgeEditRoute,
    searchRoute,
    myWorkRoute,
    ticketsRoute,
    ticketNewRoute,
    ticketRoute,
    clientsRoute,
    clientDetailRoute,
    assetsRoute,
    assetDetailRoute,
    tasksRoute,
    timeEntriesRoute,
    timeApprovalsRoute,
    approvalsRoute,
    adminRoute,
    adminUsersRoute,
    adminRolesRoute,
    adminOrganizationsRoute,
    adminTeamsRoute,
    adminDepartmentsRoute,
    adminLocationsRoute,
    adminPersonsRoute,
    adminTechnicianRolesRoute,
    adminAgentProfilesRoute,
    adminVendorsRoute,
    adminCategoriesRoute,
    adminServicesRoute,
    adminServiceAreasRoute,
    adminMaintenancePlansRoute,
    adminServiceOfferingsRoute,
    adminPrioritiesRoute,
    adminBusinessHoursRoute,
    adminHolidaysRoute,
    adminStatusesRoute,
    adminTransitionsRoute,
    adminWorkTypesRoute,
    adminTaskTypesRoute,
    adminAssetTypesRoute,
    adminCiTypesRoute,
    adminNotificationTemplatesRoute,
    adminNotificationRulesRoute,
    adminNotificationDeliveriesRoute,
    adminApprovalRulesRoute,
    adminAutomationRoute,
    billingContractsRoute,
    billingContractDetailRoute,
    billingRateCardsRoute,
    billingPeriodsRoute,
    billingPeriodDetailRoute,
    billingEntriesRoute,
    billingExceptionHoursRoute,
    billingExceptionLinesRoute,
    billingInvoicesRoute,
    billingInvoiceDetailRoute,
    billingPendingToInvoiceRoute,
    adminBillingRulesRoute,
    adminTaxRatesRoute,
    adminSequencesRoute,
    adminInvoiceNumberingRoute,
    adminFiscalIssuerRoute,
    adminBrandingRoute,
    adminSecurityRoute,
    adminCapabilitiesRoute,
    accountSecurityRoute,
    adminAuditRoute,
    adminDataExportRoute,
    adminSupportAccessRoute,
    // `/admin/feature-flags` is not mounted while no module reads a flag (see `adminManifest.ts`).
    adminSlaPoliciesRoute,
    reportsRoute,
    reportsOperationalRoute,
    reportsHoursRoute,
    reportsCostsRoute,
    reportsBillingRoute,
    reportDetailRoute,
    helpIndexRoute,
    helpPageRoute,
    catchAllRoute,
  ]),
  portalRoute.addChildren([
    portalIndexRoute,
    portalNewRoute,
    portalRequestRoute,
    portalTicketsRoute,
    portalTicketRoute,
    portalApprovalsRoute,
    portalApprovalRoute,
    portalKnowledgeRoute,
    portalKnowledgeArticleRoute,
    portalAccountSecurityRoute,
    portalHelpRoute,
  ]),
  platformRoute.addChildren([
    platformIndexRoute,
    platformTenantsRoute,
    platformTenantDetailRoute,
    platformSupportAccessRoute,
    platformAuditRoute,
    platformFeatureFlagsRoute,
    platformAccountSecurityRoute,
    platformHelpRoute,
  ]),
  helpPrintRoute,
  loginRoute,
  tenantRoute,
  forgotRoute,
  resetRoute,
  invitationRoute,
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultNotFoundComponent: NotFoundPage,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
